import { lazy, Suspense, useEffect, useState } from 'react';
import './sentry';
import * as Sentry from '@sentry/react';
import { IntlProvider } from 'react-intl';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import GlobalRoutes from 'routes/GlobalRoutes';
import ThemeWrapper from 'theme/ThemeWrapper';
import { GlobalStyle } from './GlobalStyles';
import { RoutesNames } from './routes/RoutesNames';
import SuspenseLoading from './routes/components/SuspenseLoading';
import PrivateRoutes from './routes/PrivateRoutes';
import PublicRoute from './routes/PublicRoutes';
import { RoutesData } from './routes/RoutesData';
import { NotificationsRedirectLayer } from './componentsShared/NotificationsRedirectLayer/NotificationsRedirectLayer';
import { useGetUsersQuery } from './redux/apiSlices/user.slice';
import { ImpersonateUpdatesLayer } from './components/ImpersonateUpdatesLayer';
import { SentryUserLayer } from './components/SentryUserLayer';
import ErrorFallback from './components/ErrorFallback';
import { useAppSelector } from './redux/store';
import engMessages from './locales/english.json';
import ImpersonationConfirmationModal from './routes/components/ImpersonationConfirmationModal';
import { useLazyGetPaymentMethodQuery } from 'redux/apiSlices/payment.slice';
import { usePmPermissions } from 'hooks/usePmPermissions';
import { ECapabilityKey } from 'data/types/permissions.types';

const Impersonation = lazy(() => import('pages/public/Impersonation'));
const SocialAuth = lazy(() => import('pages/public/SocialAuth'));
const Certificate = lazy(() => import('pages/public/Certificate'));

function App() {
  const usersData = useAppSelector((state) => state.usersSlice);
  const [open, setOpen] = useState(false);
  const { amplifyUser, users, userType } = usersData;
  const [path, setPath] = useState({});
  const { isLoading } = useGetUsersQuery(amplifyUser, {
    skip: !amplifyUser || amplifyUser?.challengeName === 'NEW_PASSWORD_REQUIRED',
    refetchOnMountOrArgChange: true,
  });
  const profile = users?.[userType];
  const { can } = usePmPermissions();
  const hasAccessToBillingMethod = can(ECapabilityKey.ACCESS_TO_BILLING_METHOD);

  const [getPaymentInfo] = useLazyGetPaymentMethodQuery();

  useEffect(() => {
    if (
      window.location.pathname === RoutesNames.RedirectComplaint ||
      window.location.pathname === RoutesNames.RedirectNotice
    ) {
      setPath({
        search: window.location.search,
        pathname: window.location.pathname,
      });
    }
    if (usersData.users !== null && path !== null) {
      if (path?.pathname === RoutesNames.RedirectComplaint || path?.pathname === RoutesNames.RedirectNotice) {
        const searchParams = new URLSearchParams(path?.search);

        const propertyManagementId = searchParams.get('propertyManagementId');
        const objectId = searchParams.get('objectId');

        if (usersData?.users?.landlord?.id !== propertyManagementId) {
          setOpen(true);
        } else {
          window.location.replace(
            `${
              path.pathname === RoutesNames.RedirectComplaint ? RoutesNames.Complaint : RoutesNames.Notice
            }/${objectId}`
          );
        }
      }
    }
  }, [usersData?.users]);

  useEffect(() => {
    if (profile && hasAccessToBillingMethod) {
      getPaymentInfo({
        userType: userType,
      });
    }
  }, [profile, hasAccessToBillingMethod]);

  if (isLoading) return <SuspenseLoading />;

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <IntlProvider locale='en' messages={engMessages}>
        <Router>
          <ImpersonateUpdatesLayer>
            <SentryUserLayer>
            <NotificationsRedirectLayer profile={profile}>
              <GlobalStyle />
              <ThemeWrapper>
                  <Switch>
                    {RoutesData.map((route, index) => {
                      const { path, exact, component, allowedUserTypes, isPrivate, isGlobal } = route;
                      if (isGlobal)
                        return (
                          <GlobalRoutes
                            usersData={usersData}
                            path={path}
                            exact={exact}
                            component={component}
                            key={index.toString()}
                          />
                        );

                      if (isPrivate) {
                        return (
                          <PrivateRoutes
                            usersData={usersData}
                            path={path}
                            exact={exact}
                            component={component}
                            allowedUserTypes={allowedUserTypes || ['landlord']}
                            key={index.toString()}
                          />
                        );
                      }
                      return (
                        <PublicRoute
                          usersData={usersData}
                          path={path}
                          exact={exact}
                          component={component}
                          key={index.toString()}
                        />
                      );
                    })}
                    <Route path={RoutesNames.SocialAuth}>
                      <Suspense fallback={<SuspenseLoading />}>
                        <SocialAuth />
                      </Suspense>
                    </Route>
                    <Route path={RoutesNames.Impersonation}>
                      <Suspense fallback={<SuspenseLoading />}>
                        <Impersonation />
                      </Suspense>
                    </Route>
                    <Route path={RoutesNames.Certificate}>
                      <Suspense fallback={<SuspenseLoading />}>
                        <Certificate />
                      </Suspense>
                    </Route>
                    <Route path='*'>
                      <Redirect to={RoutesNames.SignIn} />
                    </Route>
                  </Switch>
                  <ImpersonationConfirmationModal
                    usersData={usersData}
                    open={open}
                    onCancel={() => {
                      setOpen(false);
                      setPath(null);
                    }}
                    path={path}
                  />
                </ThemeWrapper>
            </NotificationsRedirectLayer>
            </SentryUserLayer>
          </ImpersonateUpdatesLayer>
        </Router>
      </IntlProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
